import React, { useState } from "react";
import { Code2, Copy, Download, Eye, FileText, X } from "lucide-react";

/**
 * Modal di visualizzazione XML con due modalità:
 *  1. Anteprima leggibile: rendering HTML pulito dei dati dell'autofattura
 *     (numero, fornitore, righe, riepilogo IVA, totali). Comodo per una
 *     verifica visiva rapida prima dell'invio.
 *  2. Sorgente XML: dump colorato del contenuto XML FatturaPA (mono-space).
 *     Utile per il commercialista che vuole ispezionare il tracciato.
 *
 * L'utente può scaricare l'XML in qualsiasi tab tramite il pulsante in
 * footer. Design coerente col resto dell'app (editorial serif per titoli,
 * font sans per body, accenti Dasabo blu #1D4ED8).
 */
const fmtEUR = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(
    Number(n || 0),
  );

const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
};

export default function XmlViewerModal({ open, onClose, invoice, extracted, xmlContent, xmlFilename, onDownload }) {
  const [tab, setTab] = useState("preview");
  const [copied, setCopied] = useState(false);

  if (!open || !invoice) return null;

  const ext = extracted || invoice.extracted || {};
  const lines = ext.line_items || [];
  const totImp = Number(ext.imponibile || 0);
  const totIva = Number(ext.imposta || 0);
  const totFatt = Number(ext.total_amount_eur || (totImp + totIva));
  const isForeignCcy = ext.original_currency && ext.original_currency !== "EUR";

  const copyXml = async () => {
    try {
      await navigator.clipboard.writeText(xmlContent || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4 sm:p-6"
      onClick={onClose}
      data-testid="xml-viewer-modal"
    >
      <div
        className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header + tabs */}
        <div className="px-6 py-4 border-b border-[#E8E4D9] flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1D4ED8] font-bold mb-1">
              Visualizza XML
            </div>
            <div className="font-display font-bold text-lg sm:text-xl text-[#0A0A0A] truncate">
              {ext.autofattura_number ? (
                <>
                  Autofattura <span className="font-serif-accent text-[#1D4ED8]">#</span>
                  {ext.autofattura_number}
                </>
              ) : (
                "Anteprima documento"
              )}
            </div>
            {xmlFilename && (
              <div className="text-xs font-mono text-[#737373] mt-1 truncate">{xmlFilename}</div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="xml-viewer-close-btn"
            className="p-1.5 rounded-full hover:bg-[#F5F2EA] text-[#4A4A4A] shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab switcher */}
        <div className="px-6 pt-3 border-b border-[#E8E4D9] flex gap-1">
          <button
            onClick={() => setTab("preview")}
            data-testid="xml-tab-preview"
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === "preview"
                ? "border-[#1D4ED8] text-[#0A0A0A]"
                : "border-transparent text-[#737373] hover:text-[#0A0A0A]"
            }`}
          >
            <Eye className="w-3.5 h-3.5" /> Anteprima leggibile
          </button>
          <button
            onClick={() => setTab("source")}
            data-testid="xml-tab-source"
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === "source"
                ? "border-[#1D4ED8] text-[#0A0A0A]"
                : "border-transparent text-[#737373] hover:text-[#0A0A0A]"
            }`}
          >
            <Code2 className="w-3.5 h-3.5" /> Sorgente XML
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {tab === "preview" ? (
            <div className="p-6 sm:p-8 space-y-8 bg-[#FAF9F6]" data-testid="xml-preview-body">
              {/* Meta grid: 3 colonne */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <MetaBox
                  label="Tipo documento"
                  value={invoice.document_type || "TD17"}
                  hint={docTypeHint(invoice.document_type)}
                />
                <MetaBox label="Numero" value={ext.autofattura_number || "—"} mono />
                <MetaBox label="Data documento" value={fmtDate(ext.data_autofattura || ext.data_ricezione)} />
                <MetaBox label="Data ricezione" value={fmtDate(ext.data_ricezione)} />
              </div>

              {/* Fornitore card */}
              <section className="bg-white border border-[#E8E4D9] rounded-lg p-5">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#1D4ED8] font-bold mb-2">
                  Cedente / Prestatore (fornitore estero)
                </div>
                <div className="font-display font-bold text-lg text-[#0A0A0A]">
                  {ext.supplier_name || "—"}
                </div>
                {ext.supplier_vat_id && (
                  <div className="text-sm text-[#4A4A4A] font-mono mt-0.5">
                    P.IVA / Tax ID: {ext.supplier_vat_id}
                  </div>
                )}
                {(ext.supplier_address || ext.supplier_city || ext.supplier_country) && (
                  <div className="text-sm text-[#4A4A4A] mt-1.5">
                    {[ext.supplier_address, ext.supplier_zip, ext.supplier_city, ext.supplier_country]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-[#E8E4D9] flex flex-wrap gap-x-6 gap-y-1 text-xs">
                  <span className="text-[#737373]">
                    Fattura originale:{" "}
                    <span className="text-[#0A0A0A] font-mono">{ext.invoice_number || "—"}</span>
                  </span>
                  <span className="text-[#737373]">
                    Data: <span className="text-[#0A0A0A]">{fmtDate(ext.invoice_date)}</span>
                  </span>
                  {ext.is_eu_supplier !== undefined && (
                    <span className="text-[#737373]">
                      Fornitore:{" "}
                      <span className="text-[#0A0A0A]">{ext.is_eu_supplier ? "UE" : "Extra-UE"}</span>
                    </span>
                  )}
                </div>
              </section>

              {/* Line items */}
              {lines.length > 0 && (
                <section>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#1D4ED8] font-bold mb-2">
                    Righe fattura
                  </div>
                  <div className="border border-[#E8E4D9] rounded-lg overflow-hidden bg-white">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F5F2EA] text-[10px] uppercase tracking-wider text-[#737373]">
                        <tr>
                          <th className="text-left px-4 py-2 font-bold">Descrizione</th>
                          <th className="text-right px-3 py-2 font-bold w-16">Q.tà</th>
                          <th className="text-right px-3 py-2 font-bold w-28">Prezzo unit.</th>
                          <th className="text-right px-3 py-2 font-bold w-20">Aliquota</th>
                          <th className="text-right px-3 py-2 font-bold w-32">Totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((li, i) => (
                          <tr key={li.id || i} className="border-t border-[#E8E4D9]/60">
                            <td className="px-4 py-2.5 text-[#0A0A0A]">{li.descrizione || "—"}</td>
                            <td className="px-3 py-2.5 text-right text-[#4A4A4A] font-mono">
                              {li.quantita || 1}
                            </td>
                            <td className="px-3 py-2.5 text-right text-[#4A4A4A] font-mono">
                              {fmtEUR(li.prezzo_unitario)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-[#4A4A4A] font-mono">
                              {li.aliquota_iva ?? 22}%
                            </td>
                            <td className="px-3 py-2.5 text-right text-[#0A0A0A] font-mono font-semibold">
                              {fmtEUR(li.prezzo_totale)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* FX info (se non EUR) */}
              {isForeignCcy && (
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-amber-800 font-bold mb-1">
                    Conversione valuta
                  </div>
                  <div className="text-[#4A4A4A]">
                    Importo originale in <span className="font-mono font-bold">{ext.original_currency}</span>
                    {" · "}Tasso {ext.fx_rate?.toFixed(4)} ({fmtDate(ext.fx_date)}, fonte {ext.fx_source || "—"})
                  </div>
                </section>
              )}

              {/* Totali */}
              <section className="bg-white border border-[#E8E4D9] rounded-lg p-5 max-w-md ml-auto">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#1D4ED8] font-bold mb-3">
                  Riepilogo IVA
                </div>
                <TotalRow label="Imponibile" value={fmtEUR(totImp)} />
                <TotalRow label={`IVA ${ext.aliquota_iva ?? 22}%`} value={fmtEUR(totIva)} />
                {ext.natura && (
                  <div className="text-xs text-[#737373] mt-1 mb-2">
                    Natura IVA: <span className="font-mono text-[#0A0A0A]">{ext.natura}</span>
                  </div>
                )}
                <div className="border-t border-[#E8E4D9] mt-3 pt-3 flex items-baseline justify-between">
                  <span className="font-display font-bold text-[#0A0A0A]">Totale documento</span>
                  <span className="font-display font-extrabold text-2xl text-[#1D4ED8] tabular-nums">
                    {fmtEUR(totFatt)}
                  </span>
                </div>
              </section>

              {/* Servizio / descrizione (se presente e non ci sono righe) */}
              {!lines.length && ext.service_description && (
                <section>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#1D4ED8] font-bold mb-2">
                    Descrizione servizio
                  </div>
                  <div className="bg-white border border-[#E8E4D9] rounded-lg p-4 text-sm text-[#4A4A4A]">
                    {ext.service_description}
                  </div>
                </section>
              )}
            </div>
          ) : (
            <div className="relative" data-testid="xml-source-body">
              <button
                onClick={copyXml}
                data-testid="xml-copy-btn"
                title="Copia negli appunti"
                className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-emerald-200 text-xs border border-white/10 transition-colors"
              >
                <Copy className="w-3 h-3" /> {copied ? "Copiato!" : "Copia"}
              </button>
              <pre className="p-4 sm:p-6 bg-zinc-950 text-emerald-300 text-xs overflow-auto leading-relaxed min-h-[400px]">
                {xmlContent || "Caricamento…"}
              </pre>
            </div>
          )}
        </div>

        {/* Footer with download */}
        <div className="px-6 py-3 border-t border-[#E8E4D9] bg-white flex items-center justify-between gap-3">
          <div className="text-xs text-[#737373] hidden sm:flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Formato FatturaPA 1.2.2 · Autofattura reverse charge
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#E8E4D9] text-[#0A0A0A] text-sm font-medium hover:bg-[#F5F2EA] transition-colors"
              data-testid="xml-viewer-close-footer-btn"
            >
              Chiudi
            </button>
            <button
              onClick={onDownload}
              data-testid="xml-viewer-download-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
            >
              <Download className="w-4 h-4" /> Scarica XML
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaBox({ label, value, hint, mono }) {
  return (
    <div className="bg-white border border-[#E8E4D9] rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-[#737373] font-bold">{label}</div>
      <div className={`mt-0.5 text-sm font-bold text-[#0A0A0A] truncate ${mono ? "font-mono" : ""}`}>
        {value}
      </div>
      {hint && <div className="text-[10px] text-[#737373] mt-0.5">{hint}</div>}
    </div>
  );
}

function TotalRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm text-[#4A4A4A] mb-1.5">
      <span>{label}</span>
      <span className="font-mono tabular-nums text-[#0A0A0A]">{value}</span>
    </div>
  );
}

function docTypeHint(dt) {
  switch ((dt || "TD17").toUpperCase()) {
    case "TD17":
      return "Servizi extra-UE";
    case "TD18":
      return "Beni intra-UE";
    case "TD19":
      return "Beni in Italia";
    case "TD04":
      return "Nota di credito";
    default:
      return "";
  }
}
